class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.bindOverlayClose();
    this.setHeaderCartIconAccessibility();
  }

  bindOverlayClose() {
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay && !overlay.dataset.boundClose) {
      overlay.dataset.boundClose = 'true';
      overlay.addEventListener('click', this.close.bind(this));
    }
  }

  setHeaderCartIconAccessibility() {
    const selectors = [
      '#cart-icon-bubble',
      '.automotivebypass-header__cart',
      '.automotivebypass-cart',
      'a[href="/cart"]'
    ];
    const cartLinks = document.querySelectorAll(selectors.join(','));
    cartLinks.forEach((cartLink) => {
      if (cartLink.closest('cart-drawer') || cartLink.dataset.cartDrawerBound === 'true') return;
      cartLink.dataset.cartDrawerBound = 'true';
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        this.open(cartLink);
      });
      cartLink.addEventListener('keydown', (event) => {
        if (event.code && event.code.toUpperCase() === 'SPACE') {
          event.preventDefault();
          this.open(cartLink);
        }
      });
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (typeof trapFocus === 'function' && containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
    if (window.location.pathname === '/cart' || window.location.pathname.endsWith('/cart')) {
      const ref = document.referrer;
      if (ref && ref.indexOf(window.location.host) !== -1 && !ref.endsWith('/cart')) {
        window.history.back();
      } else {
        window.location.href = '/collections/all';
      }
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling && cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (typeof onKeyUpEscape === 'function') {
      cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
    }
  }

  renderContents(parsedState, shouldOpen = true) {
    const inner = this.querySelector('.drawer__inner');
    if (inner && inner.classList.contains('is-empty')) {
      inner.classList.remove('is-empty');
    }
    if (parsedState && parsedState.id) {
      this.productId = parsedState.id;
    }

    if (parsedState && parsedState.sections) {
      if (parsedState.sections['cart-drawer']) {
        const parsedDoc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
        const newCartDrawer = parsedDoc.querySelector('cart-drawer');
        const currentCartDrawerEl = this.querySelector('#CartDrawer');
        const newCartDrawerEl = parsedDoc.querySelector('#CartDrawer');
        if (newCartDrawer) {
          this.classList.toggle('is-empty', newCartDrawer.classList.contains('is-empty'));
        }
        if (currentCartDrawerEl && newCartDrawerEl) {
          currentCartDrawerEl.innerHTML = newCartDrawerEl.innerHTML;
        }
      }

      if (parsedState.sections['cart-icon-bubble']) {
        this.syncHeaderCartBadgesFromSection(parsedState.sections['cart-icon-bubble']);
      }
    }

    this.bindOverlayClose();

    if (shouldOpen) {
      setTimeout(() => {
        this.bindOverlayClose();
        this.open();
      });
    }
  }

  syncHeaderCartBadgesFromSection(bubbleHtml) {
    const doc = new DOMParser().parseFromString(bubbleHtml, 'text/html');
    const countText = doc.querySelector('.cart-count-bubble span[aria-hidden="true"]')?.textContent?.trim() || '';
    const count = parseInt(countText, 10) || 0;
    CartDrawer.updateAllHeaderCartBadges(count);
  }

  static updateAllHeaderCartBadges(count) {
    const num = parseInt(count, 10) || 0;
    document.querySelectorAll('.automotivebypass-header__cart, .automotivebypass-cart').forEach((cartAnchor) => {
      let badge = cartAnchor.querySelector('.automotivebypass-header__cart-count, .cart-count');
      if (num > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = cartAnchor.classList.contains('automotivebypass-header__cart')
            ? 'automotivebypass-header__cart-count'
            : 'cart-count';
          cartAnchor.appendChild(badge);
        }
        badge.textContent = String(num);
        badge.hidden = false;
        badge.style.display = '';
      } else if (badge) {
        badge.textContent = '';
        badge.hidden = true;
        badge.style.display = 'none';
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

/* ==========================================================================
   Real-Time Add-to-Cart & Native Cart Sync Controller
   ========================================================================== */
(function () {
  function refreshAndOpenDrawer(triggerEl, shouldOpen) {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return Promise.resolve();
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return Promise.all([
      fetch(cartUrl + '?sections=cart-drawer,cart-icon-bubble', {
        headers: { Accept: 'application/json' }
      }).then((r) => r.json()),
      fetch(cartUrl + '.js', {
        headers: { Accept: 'application/json' }
      }).then((r) => r.json())
    ])
      .then(([sectionsData, cartJson]) => {
        if (cartJson && typeof cartJson.item_count === 'number') {
          drawer.classList.toggle('is-empty', cartJson.item_count === 0);
          CartDrawer.updateAllHeaderCartBadges(cartJson.item_count);
        }
        drawer.renderContents({ sections: sectionsData }, shouldOpen !== false);
      })
      .catch((err) => {
        console.error('[Automotivebypass CartDrawer] Refresh error:', err);
      });
  }

  /* Listen to Dawn pubsub cart updates to keep header badge & drawer synced */
  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.setHeaderCartIconAccessibility();
    }

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event && event.cartData && typeof event.cartData.item_count === 'number') {
          CartDrawer.updateAllHeaderCartBadges(event.cartData.item_count);
        } else {
          fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js')
            .then((r) => r.json())
            .then((c) => {
              if (c && typeof c.item_count === 'number') {
                CartDrawer.updateAllHeaderCartBadges(c.item_count);
              }
            })
            .catch(() => {});
        }
      });
    }

    /* If user visits /cart directly, automatically open the half-page slide-out Cart Drawer */
    if (window.location.pathname === '/cart' || window.location.pathname.endsWith('/cart')) {
      if (drawer) {
        setTimeout(() => {
          drawer.open();
        }, 60);
      }
    }
  });

  /* Intercept custom Add-to-Cart forms (such as Automotivebypass PDP & Brand Cards)
     that are not wrapped in <product-form>, submitting via AJAX and opening Cart Drawer */
  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      if (form.closest('product-form')) return; // Handled natively by product-form.js
      const action = form.getAttribute('action') || '';
      if (action.indexOf('/cart/add') === -1) return;

      /* Respect PDP vehicle mismatch modal if another listener called preventDefault() */
      if (event.defaultPrevented) return;

      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      event.preventDefault();

      const submitBtn = form.querySelector('[type="submit"], button[name="add"]');
      const stickyBtn = document.querySelector('[data-ab-sticky-add]');
      const origText = submitBtn ? submitBtn.textContent : '';

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
      }
      if (stickyBtn) {
        stickyBtn.disabled = true;
      }

      const formData = new FormData(form);
      formData.append('sections', 'cart-drawer,cart-icon-bubble');
      formData.append('sections_url', window.location.pathname);

      const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
      fetch(addUrl + '.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json'
        },
        body: formData
      })
        .then((response) => response.json())
        .then((res) => {
          if (res.status && res.status !== 200) {
            throw new Error(res.description || res.message || 'Could not add item to cart');
          }
          if (submitBtn) {
            drawer.setActiveElement(submitBtn);
          }
          if (res.sections && res.sections['cart-drawer']) {
            drawer.classList.remove('is-empty');
            drawer.renderContents(res, true);
            fetch(((window.routes && window.routes.cart_url) || '/cart') + '.js')
              .then((r) => r.json())
              .then((c) => {
                if (c && typeof c.item_count === 'number') {
                  CartDrawer.updateAllHeaderCartBadges(c.item_count);
                }
              })
              .catch(() => {});
          } else {
            return refreshAndOpenDrawer(submitBtn, true);
          }
        })
        .catch((err) => {
          console.error('[Automotivebypass AddToCart]', err);
          HTMLFormElement.prototype.submit.call(form);
        })
        .finally(() => {
          if (submitBtn) {
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
            if (origText) submitBtn.textContent = origText;
          }
          if (stickyBtn && submitBtn) {
            stickyBtn.disabled = submitBtn.disabled;
          }
        });
    },
    false
  );
})();
